import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { Alert, Select } from "ant-design-vue";
import { fmtMsg } from "commonui";
import {
	LFCodeUnitCountResponseModel,
	TotalReportManagerService,
	LFCodeUnitCountParamsModel
} from "@/services/text-guardian/total-report";
import { GridRef } from "@/components/wijmo-grid/grid/grid";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { LanguageSkillModel, VCSLevelModel } from "@/models/title-list-model";
import { CommonLocale, TotalReportLocale } from "@/locales/localeid";
import { disabledDueToAccessLevel, getRouteAccessLevel, getSkillSelected, isAnyVersionSelected } from "@/util/func";
import { AllLanguageSkill } from "@/util/constants";
import { VersionGroupModule } from "@/modules/versiongroup";
import { sortTagList, insertSequence } from "@/util/func";
import { HAlign, IWorkbookCell } from "wijmo/wijmo.xlsx";
import PageTitle from "@/components/page-title/page-title.vue";
import { AccessLevel } from "@/util/enum";

@Component({
	components: {
		Grid,
		Select,
		Option: Select.Option,
		Alert,
		PageTitle
	}
})
export default class TotalReport extends Vue {
	public loading: boolean = false;
	public titleListModule = getModule(TitleListState);
	public readonly allLanguageSkill = AllLanguageSkill;
	public codeUnitCount: LFCodeUnitCountResponseModel[] = [];
	public skillSelected: number[] = [];
	public totalReportService = TotalReportManagerService();
	@Ref() readonly gridCompRef!: GridRef;
	public format = fmtMsg;
	public locale = TotalReportLocale;
	public commonLocale = CommonLocale;
	private versionGroupModule = VersionGroupModule; // For watch need to create local reference
	public noVersion: boolean = false;
	public accessLevel = AccessLevel;

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	created() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		}

		this.skillSelected = [this.allLanguageSkill];
		this.fetchLFCodeUnitCount(this.skillSelected);
	}

	mounted() {
		const codeUnitCount = this.$route.meta.codeUnitCount;
		this.codeUnitCount = this.calculateTotalForCodeUnitCount(codeUnitCount);
		this.versionGroupModule.setDisabled(false);
	}

	destroyed() {
		this.versionGroupModule.setDisabled(true);
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	isVersionUpdated() {
		if (isAnyVersionSelected()) {
			this.noVersion = false;
			this.fetchLFCodeUnitCount(this.skillSelected);
		} else {
			this.noVersion = true;
		}
	}

	get levels(): VCSLevelModel[] {
		const levels = this.titleListModule.levels.sort((a, b): number => {
			if (a.curriculumType > b.curriculumType) {
				return -1;
			} else if (a.curriculumType < b.curriculumType) {
				return 1;
			} else if (a.curriculumType === b.curriculumType && a.unit < b.unit) {
				return -1;
			} else {
				return 0;
			}
		});
		return levels;
	}

	get languageSkills(): LanguageSkillModel[] {
		return this.titleListModule.languageSkills;
	}

	onSkillChange(selectedValues) {
		this.skillSelected = getSkillSelected(selectedValues);
		this.fetchLFCodeUnitCount(this.skillSelected);
	}

	fetchLFCodeUnitCount(selectedlanguageSkillIds) {
		this.loading = true;

		const params: LFCodeUnitCountParamsModel = {
			languageSkillIds: selectedlanguageSkillIds.join(";"),
			gsVersionGroupId:
				VersionGroupModule.selectedGsVersion !== "-1" ? VersionGroupModule.selectedGsVersion : null,
			lsVersionGroupId:
				VersionGroupModule.selectedLsVersion !== "-1" ? VersionGroupModule.selectedLsVersion : null
		};

		return this.totalReportService.getLFCodeUnitCount(params).then((codeUnitCountdata) => {
			let data = this.calculateTotalForCodeUnitCount(codeUnitCountdata);
			data = sortTagList(data, "Code");
			data = insertSequence(data);

			this.codeUnitCount = data;
			this.loading = false;
			return Promise.resolve();
		});
	}

	calculateTotalForCodeUnitCount(codeUnitData): LFCodeUnitCountResponseModel[] {
		let codeUnitCountWithTotal: LFCodeUnitCountResponseModel[] = [];
		if (Array.isArray(codeUnitData)) {
			codeUnitCountWithTotal = codeUnitData.map((data) => {
				let total = 0;

				Object.entries(data).forEach(([key, value]) => {
					/* 
						The total is being calculated on the condition that the type of value is number.
						Since first two columns (i.e Code and Description) have values as string type 
						and other columns are dynamic (Unit columns having numeric values)
					*/
					if (typeof value === "number") {
						total += value;
					}
				});

				return { ...data, total };
			});
		}
		return codeUnitCountWithTotal;
	}

	get exportOptions() {
		return {
			formatCell: (cell: IWorkbookCell, binding: string, record: LFCodeUnitCountResponseModel) => {
				if (binding === "sequence") {
					cell.value = record.Code;
					cell.style = { hAlign: HAlign.Left, ...cell.style }; // to left align.
				}
			},
			filename: this.format(TotalReportLocale.TotalReportExportTitle)
		};
	}
}
